import { getString, truncate } from "@/utils/string.js";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { showPhenoDescriptionSwal } from "@/swals/pheno.js";
import { getPval, get3Point } from "@/utils/statistics.js";
import Swal from "sweetalert2";

function getPvalWrapper(cell) {
  return getPval(cell.getValue());
}

function get3PointWrapper(cell) {
  return get3Point(cell.getValue());
}

function getStringWrapper(cell) {
  return getString(cell.getValue());
}

let initialLoad = 0;
let selectedRow = null;

export function buildPhenoTableData(
  updatorCllback,
  tableContainerId,
  searchUrl,
  id
) {
  var table = new Tabulator(`#${tableContainerId}`, {
    height: "250px",
    layout: "fitColumns",
    responsiveLayout: true,
    resizableColumns: true,
    selectableRows: true,
    selectableRowsPersistence: true,
    ajaxURL: searchUrl,
    ajaxResponse: function (url, params, response) {
      if (response.data.has_time == false) {
        Swal.fire({
          title: '<p style="font-family: Ubuntu">Session Expired</p>',
          html: '<p style="font-family:Ubuntu; font-size: 16px;">Your session has expired. <br>Please refresh the page.</p>',
          icon: "warning",
          iconColor: "#A51C30",
          confirmButtonText: "Close",
          allowOutsideClick: false,
        });
      } else {
        initialLoad += 1;
        return response;
      }
    },
    ajaxParams: { id: id, client_id: localStorage.getItem("client_id") },
    progressiveLoad: "scroll",
    ajaxConfig: "POST",
    filterMode: "remote",
    sortMode: "remote",
    ajaxContentType: "json",
    paginationSize: 25,
    placeholder: "No Data",
    columns: [
      {
        title: "Pheno. ID",
        field: "PhenoID",
        sorter: "string",
        hozAlign: "center",
        headerFilter: "input",
        headerPopup:
          "<p style='font-family: Ubuntu;  padding: 3px;'>Phenotype ID: Unique identifier for phenotype in AGES</p>",
        headerHozAlign: "center",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',

        headerFilterPlaceholder: "Contains",
      },
      {
        title: "−log<sub>10</sub>(P<sub>β</sub>)",
        field: "mlog10P",
        formatter: getPvalWrapper,
        sorter: "number",
        headerPopup:
          "<p style='font-family: Ubuntu;  padding: 3px;'>The P-value quantifies the statistical significance of the variant's association with the specified phenotype. It is converted to a -log<sub>10</sub> scale, where more significant values appear larger, and less significant values appear smaller.<p>",
        hozAlign: "center",
        headerHozAlign: "center",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',

        headerFilter: minMaxFilterEditor,
        headerFilterFunc: minMaxFilterFunction,
        headerFilterLiveFilter: false,
      },
      {
        title: "β",
        field: "BETA",
        formatter: get3PointWrapper,
        sorter: "number",
        hozAlign: "center",
        headerHozAlign: "center",
        headerPopup:
          "<p style='font-family: Ubuntu; padding: 3px;'>The effect size of the alternative allele (ALT) reflects the strength of the statistical association between the variant and the phenotype. The ALT allele is the second allele listed in the variant ID format (CHROM_POS_REF_ALT) at the top of this page.</p>",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',
        headerFilter: minMaxFilterEditor,
        headerFilterFunc: minMaxFilterFunction,
        headerFilterLiveFilter: false,
      },
      {
        title: "Description",
        formatter: getStringWrapper,
        field: "description",
        hozAlign: "center",
        headerHozAlign: "center",
        headerPopup:
        "<p style='font-family: Ubuntu;  padding: 3px;'>Phenotype Description field from the Pan-UK Biobank</p>",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',
        headerFilter: "input",
        headerFilterPlaceholder: "Contains",
      },
      {
        title: "Coding",
        formatter: getStringWrapper,
        field: "coding_description",
        hozAlign: "center",
        headerPopup:
        "<p style='font-family: Ubuntu;  padding: 3px;'>Phenotype Coding field from the Pan-UK Biobank</p>",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',

        headerHozAlign: "center",
        headerFilter: "input",
        headerFilterPlaceholder: "Contains",
      },
      {
        title: "QC",
        formatter: getStringWrapper,
        field: "phenotype_qc_EUR",
        sorter: "string",
        headerHozAlign: "center",
        headerPopup:
        "<p style='font-family: Ubuntu;  padding: 3px;'>Phenotype Quality Control field from Pan-UK Biobank</p>",
        headerPopupIcon:
          '<span class="material-symbols-outlined" style="font-size: 15px; color: #A51C30;">info</span>',

        hozAlign: "center",
        headerFilter: "input",
        headerFilterPlaceholder: "Contains",
      },
    ],
  });
  setInterval(() => {
    if (initialLoad === 1) {
      const firstRow = table.getRows()[0];
      table.selectRow(firstRow);
      selectedRow = firstRow.getData().PhenoID;
      initialLoad += 1;
    } else {
      const selectedRowObj = table
        .getRows()
        .find((row) => row.getData().PhenoID === selectedRow);
      if (selectedRowObj) {
        table.selectRow(selectedRowObj);
      }
    }
  }, 256);
  table.on("rowClick", function (e, row) {
    e.preventDefault();
    table.deselectRow();
    table.selectRow(row);
    selectedRow = row.getData().PhenoID;
    updatorCllback(row.getData().PhenoID);
  });
  table.on("rowTapHold", function (e, row) {
    e.preventDefault();
    showPhenoDescriptionSwal(row.getData());
  });
  table.on("rowContext", function (e, row) {
    e.preventDefault();
    showPhenoDescriptionSwal(row.getData());
  });
}

var minMaxFilterEditor = function (
  cell,
  onRendered,
  success,
  cancel,
  editorParams
) {
  var end;
  var container = document.createElement("span");

  var start = document.createElement("input");
  start.setAttribute("type", "number");
  start.setAttribute("placeholder", "Min");
  start.setAttribute("min", 0);
  start.setAttribute("max", 100);
  start.style.padding = "4px";
  start.style.margin = "4px";
  start.style.borderRadius = "5px";
  start.style.border = "1px solid black";
  start.style.textAlign = "center";
  start.style.width = "45%";
  start.style.boxSizing = "border-box";

  start.value = cell.getValue();

  function buildValues() {
    success({
      start: start.value,
      end: end.value,
    });
  }

  function keypress(e) {
    if (e.keyCode == 13) {
      buildValues();
    }

    if (e.keyCode == 27) {
      cancel();
    }
  }

  end = start.cloneNode();
  end.setAttribute("placeholder", "Max");

  start.addEventListener("change", buildValues);
  start.addEventListener("blur", buildValues);
  start.addEventListener("keydown", keypress);

  end.addEventListener("change", buildValues);
  end.addEventListener("blur", buildValues);
  end.addEventListener("keydown", keypress);

  container.appendChild(start);
  container.appendChild(end);

  return container;
};

function minMaxFilterFunction(headerValue, rowValue, rowData, filterParams) {
  if (rowValue) {
    if (headerValue.start != "") {
      if (headerValue.end != "") {
        return rowValue >= headerValue.start && rowValue <= headerValue.end;
      } else {
        return rowValue >= headerValue.start;
      }
    } else {
      if (headerValue.end != "") {
        return rowValue <= headerValue.end;
      }
    }
  }
  return true;
}
